// The card-on-file section of a patient's expanded row: the card (or none), its
// consent, and the patient's Stripe card payments with what is still refundable.
//
// This panel only opens dialogs. "Charge card…", "Remove card…" and "Refund…" each
// open a confirm (ChargeCardModal, the remove confirm below, RefundModal); nothing
// here moves money or changes a consent on a single click.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { formatCurrency } from "../../utils/formatCurrency";
import ChargeCardModal from "./ChargeCardModal";
import RefundModal from "./RefundModal";
import {
  centsToDollars,
  chargeRefusal,
  fetchCard,
  fetchCardPayments,
  formatDay,
  formatWhen,
  revokeCard,
} from "./api";
import "./Collections.css";

function RemoveCardConfirm({ card, onCancel, onConfirm, busy, error }) {
  return (
    <div className="coll-modal-backdrop" onClick={() => !busy && onCancel()}>
      <div
        className="coll-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="coll-remove-card-title"
      >
        <div className="coll-modal-header">
          <h2 id="coll-remove-card-title">Remove card on file</h2>
          <button
            className="coll-modal-close"
            onClick={onCancel}
            disabled={busy}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="coll-modal-body">
          <p className="coll-modal-lede">
            This withdraws the patient's consent for{" "}
            <strong>
              {(card.brand || "Card").toUpperCase()} ···· {card.last4}
            </strong>{" "}
            and removes the card in Stripe. It cannot be charged again; the
            patient can add a card from their statement link.
          </p>
          {error && (
            <div className="coll-message error" role="alert">
              {error}
            </div>
          )}
        </div>
        <div className="coll-modal-footer">
          <button
            className="btn btn-secondary btn-sm"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            className="btn btn-danger btn-sm coll-danger"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? "Removing…" : `Remove ···· ${card.last4}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CardPanel({
  practiceId,
  patientId,
  patientName,
  readOnly = false,
  onChanged,
}) {
  const [summary, setSummary] = useState(null);
  const [payments, setPayments] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [charging, setCharging] = useState(false);
  const [refunding, setRefunding] = useState(null); // payment
  const [removing, setRemoving] = useState(false);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [removeError, setRemoveError] = useState(null);
  // What this panel asked Stripe for and has not seen land yet. The server's figures
  // do not move until the webhook posts (seconds to minutes), so without this the
  // panel redraws "Refundable $300.00" straight after a $100 refund and invites a
  // second one — which Stripe would accept, because $200 is still refundable there.
  const [refundsRequested, setRefundsRequested] = useState(() => new Set());
  const [chargeRequested, setChargeRequested] = useState(null); // {chargeableCents}
  const removeInFlight = useRef(false);
  const ticket = useRef(0);

  const load = useCallback(async () => {
    const mine = (ticket.current += 1);
    setError(null);
    try {
      const [card, rows] = await Promise.all([
        fetchCard(practiceId, patientId),
        fetchCardPayments(practiceId, patientId),
      ]);
      if (mine !== ticket.current) return;
      setSummary(card);
      setPayments(rows);
      // A charge has landed once what the rules allow has FALLEN. Only a decrease
      // counts: an unrelated posting can raise it while the webhook is still in
      // flight, and clearing on that would re-offer the button mid-charge.
      setChargeRequested((pending) =>
        pending && card.chargeableCents < pending.chargeableCents
          ? null
          : pending,
      );
      // A refund has landed once its payment is gone, no longer posted, or reduced.
      setRefundsRequested((pending) => {
        if (pending.size === 0) return pending;
        const next = new Set(
          [...pending].filter((id) => {
            const row = rows.find((r) => r.id === id);
            return !!row && row.status === "posted" && row.refundableCents > 0;
          }),
        );
        return next.size === pending.size ? pending : next;
      });
    } catch (err) {
      if (mine !== ticket.current) return;
      setError(err.message);
    }
  }, [practiceId, patientId]);

  useEffect(() => {
    setSummary(null);
    setPayments(null);
    load();
  }, [load]);

  const afterWrite = () => {
    load();
    if (onChanged) onChanged();
  };

  const remove = async () => {
    if (removeInFlight.current) return;
    removeInFlight.current = true;
    setRemoveBusy(true);
    setRemoveError(null);
    try {
      await revokeCard(practiceId, patientId);
      setRemoving(false);
      setNotice("Card removed. It can no longer be charged.");
      afterWrite();
    } catch (err) {
      setRemoveError(err.message);
    } finally {
      removeInFlight.current = false;
      setRemoveBusy(false);
    }
  };

  const card =
    summary && summary.card && summary.card.live ? summary.card : null;
  // A card with no last four cannot be confirmed against: the dialog would restate
  // "···· —", and the server's expected_last4 check passes null against null.
  const chargeable = !!card && !!card.last4 && summary.chargeableCents > 0;

  return (
    <div className="coll-card-panel">
      <h3>Card on file</h3>
      {error && (
        <div className="coll-message error" role="alert">
          {error}
        </div>
      )}
      {notice && (
        <div className="coll-message success">
          <span>{notice}</span>
          <button
            className="coll-message-close"
            aria-label="Dismiss"
            onClick={() => setNotice(null)}
          >
            ×
          </button>
        </div>
      )}
      {!error && !summary && <p className="coll-muted">Loading…</p>}

      {summary && !summary.enabled && (
        <p className="coll-muted">
          Card payments are not set up for this environment.
        </p>
      )}

      {summary && summary.enabled && !card && (
        <p className="coll-muted">
          No card on file. The patient can add one from their statement link.
        </p>
      )}

      {summary && card && (
        <div className="coll-card-line">
          <span className="coll-nowrap">
            <strong>
              {(card.brand || "Card").toUpperCase()} ···· {card.last4}
            </strong>
            {card.exp ? ` exp ${card.exp}` : ""}
          </span>
          <span className="coll-muted">
            consented {formatWhen(card.consentedAt)}
            {card.consentTextVersion != null
              ? ` · wording v${card.consentTextVersion}`
              : ""}
          </span>
          {!readOnly && summary.enabled && (
            <span className="coll-card-actions">
              {chargeRequested ? (
                <span className="coll-chip warn">
                  Charge submitted — waiting for Stripe
                </span>
              ) : (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => setCharging(true)}
                  disabled={!chargeable}
                  title={
                    card && !card.last4
                      ? "Atlas has no last four for this card, so a charge cannot be confirmed against it"
                      : summary.chargeableCents <= 0
                        ? chargeRefusal(summary)
                        : "Review the amount and consent before anything is charged"
                  }
                >
                  Charge card…
                </button>
              )}
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  setRemoveError(null);
                  setRemoving(true);
                }}
              >
                Remove card…
              </button>
            </span>
          )}
        </div>
      )}

      {/* The reasons the refusal points at. Without these the tooltip on a dead
          button names a list that is not on screen — and the charge dialog, which
          does render them, cannot be opened while the button is disabled. */}
      {summary && summary.chargeableCents <= 0 && summary.held.length > 0 && (
        <div className="coll-held" data-testid="panel-held">
          <strong>Not chargeable under this practice's rules</strong>
          <ul>
            {summary.held.map((h) => (
              <li key={h.claimId}>
                {h.dos ? formatDay(h.dos) : "—"} ·{" "}
                {formatCurrency(centsToDollars(h.balanceCents))} — {h.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {payments && payments.length > 0 && (
        <table className="coll-mini-table">
          <thead>
            <tr>
              <th>Card payment</th>
              <th className="num">Amount</th>
              <th>Status</th>
              <th className="num">Refundable</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id}>
                <td>{formatDay(p.paymentDate)}</td>
                <td className="num">
                  {formatCurrency(centsToDollars(p.amountCents))}
                </td>
                <td>{p.status || "—"}</td>
                <td className="num">
                  {formatCurrency(centsToDollars(p.refundableCents))}
                </td>
                <td className="coll-actions">
                  {refundsRequested.has(p.id) ? (
                    <span className="coll-chip warn">
                      Refund requested — waiting for Stripe
                    </span>
                  ) : (
                    !readOnly &&
                    p.refundableCents > 0 && (
                      <>
                        {p.refundRequested && (
                          <div className="coll-muted">
                            A refund was asked for on this payment. Check it
                            landed before refunding again.
                          </div>
                        )}
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setRefunding(p)}
                        >
                          Refund…
                        </button>
                      </>
                    )
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {charging && (
        <ChargeCardModal
          practiceId={practiceId}
          patientId={patientId}
          patientName={patientName}
          onClose={(attempted) => {
            setCharging(false);
            if (attempted) afterWrite();
          }}
          onCharged={(result, { amountCents, last4 }) => {
            setCharging(false);
            // The modal charged against its own, fresher figure; seeding from this
            // panel's older one would clear the flag on the next read.
            setChargeRequested({
              chargeableCents:
                (result && result.chargeableCentsAtCharge) ??
                summary.chargeableCents,
            });
            // Not "Charged": Stripe answered with an intent status (which can be
            // `processing`), and only the webhook decides what posts.
            setNotice(
              `Charge of ${formatCurrency(centsToDollars(amountCents))} to ···· ${last4} submitted (${(result && result.status) || "sent to Stripe"}). It posts when Stripe confirms it.`,
            );
            afterWrite();
          }}
        />
      )}

      {refunding && (
        <RefundModal
          practiceId={practiceId}
          patientId={patientId}
          payment={refunding}
          onClose={(attempted) => {
            setRefunding(null);
            if (attempted) afterWrite();
          }}
          onRefunded={(result, { amountCents }) => {
            const paymentId = refunding.id;
            setRefunding(null);
            setRefundsRequested((pending) => new Set(pending).add(paymentId));
            setNotice(
              `Refund of ${formatCurrency(centsToDollars(amountCents))} requested (${(result && result.status) || "sent to Stripe"}). The reversal posts when Stripe confirms it.`,
            );
            afterWrite();
          }}
        />
      )}

      {removing && card && (
        <RemoveCardConfirm
          card={card}
          busy={removeBusy}
          error={removeError}
          onCancel={() => setRemoving(false)}
          onConfirm={remove}
        />
      )}
    </div>
  );
}
